import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building,
  Building2,
  Users,
  UserCircle,
  Briefcase,
  Folder,
  FolderOpen,
  LayoutDashboard,
  LayoutGrid,
  Network,
  GitBranch,
  Tag,
  Bookmark,
  Star,
  Heart,
  TreePine,
  Settings,
  Sliders,
  Filter,
  Layers,
  Box,
  Package,
  Container,
  FileText,
  File,
  Scroll,
  ChartBar,
  BarChart3,
  PieChart,
  Clock,
  Timer,
  Calendar,
  CalendarDays,
  Search,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Input } from '@/shared/components/ui/input';
import { cn } from '@/core/lib/utils';

const ICONS = [
  { name: 'Building', Icon: Building },
  { name: 'Building2', Icon: Building2 },
  { name: 'Users', Icon: Users },
  { name: 'UserCircle', Icon: UserCircle },
  { name: 'Briefcase', Icon: Briefcase },
  { name: 'Folder', Icon: Folder },
  { name: 'FolderOpen', Icon: FolderOpen },
  { name: 'LayoutDashboard', Icon: LayoutDashboard },
  { name: 'LayoutGrid', Icon: LayoutGrid },
  { name: 'Network', Icon: Network },
  { name: 'GitBranch', Icon: GitBranch },
  { name: 'Tag', Icon: Tag },
  { name: 'Bookmark', Icon: Bookmark },
  { name: 'Star', Icon: Star },
  { name: 'Heart', Icon: Heart },
  { name: 'TreePine', Icon: TreePine },
  { name: 'Network', Icon: Network },
  { name: 'Settings', Icon: Settings },
  { name: 'Sliders', Icon: Sliders },
  { name: 'Filter', Icon: Filter },
  { name: 'Layers', Icon: Layers },
  { name: 'Box', Icon: Box },
  { name: 'Package', Icon: Package },
  { name: 'Container', Icon: Container },
  { name: 'FileText', Icon: FileText },
  { name: 'File', Icon: File },
  { name: 'Scroll', Icon: Scroll },
  { name: 'ChartBar', Icon: ChartBar },
  { name: 'BarChart3', Icon: BarChart3 },
  { name: 'PieChart', Icon: PieChart },
  { name: 'Clock', Icon: Clock },
  { name: 'Timer', Icon: Timer },
  { name: 'Calendar', Icon: Calendar },
  { name: 'CalendarDays', Icon: CalendarDays },
] as const;

export type IconName = (typeof ICONS)[number]['name'];

interface OrganizationUnitIconPickerProps {
  value?: string;
  onSelect: (iconName: string) => void;
  trigger: React.ReactNode;
  disabled?: boolean;
}

export function OrganizationUnitIconPicker({
  value,
  onSelect,
  trigger,
  disabled,
}: OrganizationUnitIconPickerProps) {
  const { t } = useTranslation('organizationUnits');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filteredIcons = ICONS.filter((icon) =>
    icon.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = (iconName: string) => {
    onSelect(iconName);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
          <Input
            placeholder={t('iconPicker.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        </div>
        <div className="h-64 overflow-auto p-2">
          <div className="grid grid-cols-5 gap-1">
            {filteredIcons.map(({ name, Icon }) => (
              <button
                key={name}
                type="button"
                onClick={() => handleSelect(name)}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                  'hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  value === name && 'bg-muted',
                )}
                title={name}
              >
                <Icon className={cn('h-5 w-5', value === name && 'text-primary')} />
                {value === name && <span className="sr-only">{name} selected</span>}
              </button>
            ))}
          </div>
        </div>
        {filteredIcons.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {t('iconPicker.noResults')}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function OrganizationUnitIcon({
  name,
  className,
  style,
}: {
  name: string;
  className?: string;
  style?: React.CSSProperties;
}) {
  const Icon = (
    LucideIcons as unknown as Record<
      string,
      React.ComponentType<{ className?: string; style?: React.CSSProperties }>
    >
  )[name];
  if (!Icon) return null;
  return <Icon className={className} style={style} />;
}
